import type { CreditPackage } from '@frontend/api-types';
import type { StrictOmit } from '@frontend/duck-tape';
import { formatCurrencyNatural, makeCent } from '@frontend/duck-tape';
import { useFeatureValue } from '@frontend/react';
import type { SubscriptionTerminologyConfig } from '../terminology';

type DescriptionGeneratorOptions = StrictOmit<CreditPackage, 'description'> & {
  unitTerm: string;
};

const _defaultDescriptionGenerator = ({ amount, priceCent, unitTerm }: DescriptionGeneratorOptions): string => {
  const perCredit = makeCent(amount > 0 ? Math.round(Number(priceCent) / amount) : 0);
  return `${formatCurrencyNatural(perCredit)}/${unitTerm}`;
};

/**
 * Fetch credit purchase packages and ensure each has a description.
 *
 * @param {Object} options - Configuration options.
 * @param {SubscriptionTerminologyConfig} options.pricingTerminology - Terminology config for unit term.
 * @param {function} [options.descriptionGenerator] - Custom function to generate package descriptions.
 * @returns {CreditPackage[]} Filtered and completed packages.
 */
export const useCreditPackages = ({
  descriptionGenerator = _defaultDescriptionGenerator,
  pricingTerminology,
}: {
  descriptionGenerator?: typeof _defaultDescriptionGenerator;
  pricingTerminology: SubscriptionTerminologyConfig;
}): CreditPackage[] => {
  const { value: packages } = useFeatureValue('credit_purchase_packages', []);

  return (
    packages
      ?.filter((p) => p.credits && p.price_cents)
      .map<CreditPackage>((p) => {
        const amount = p.credits ?? 0;
        const priceCent = p.price_cents ?? makeCent(0);
        return {
          amount,
          description:
            p.description ?? descriptionGenerator({ amount, priceCent, unitTerm: pricingTerminology.unit_term }),
          originalPriceCent: p.original_price_cents,
          priceCent,
        };
      }) ?? []
  );
};
